import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api/client';
import { LoaderSymbol, BusyIcon } from '../../components/loading';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, ToolbarDivider } from '../../components/AppToolbar';
import { AppDialog } from '../../components/AppDialog';
import { DEFAULT_DATE_RANGE } from '../../components/listDefaults';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { useNotify } from '../../components/notifications';
import { useDeleteFlow, useConfirm } from '../../components/confirm';
import { useAuthStore } from '../../auth/store';
import { FormTitle } from '../../components/FormTitle';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import { consumePendingRecord } from '../../layouts/navigateTo';
import { useNavActions } from '../../hooks/useNavActions';
import SampleRegistrationDetailPage, { SAMPLE_REGISTRATION_FORM, type StepIndex }
  from './sampleRegistration/SampleRegistrationDetailPage';
import './sampleRegistration/sampleRegistration.css';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface SampleRegRow {
  jobId:             string;
  projectName:       string;
  client:            string;
  receivedDate:      string | null;
  registrationType:  string;
  dueDate:           string | null;
  statusName:        string;
  statusId:          string;
  createdDate:       string | null;
  updatedDate:       string | null;
}

// 'All' sentinel + any exact StatusDefinition.Status value (data-driven chips,
// G5 parity — one chip per real status so counts match G5's per-status filter).
type StatusFilter  = string;
type DateRangeKey  = '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'All';
type ReportType    = 'COC Report' | 'Pre Invoice' | 'RTCR';

/** Optional values from the selected list row shown while detail data loads. */
interface DetailSeed {
  client:       string;
  projectName:  string;
  status:       string;
  receivedDate: string | null;
  dueDate:      string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// PDF generation (jsPDF + autoTable — runs entirely client-side, instant open)
// ─────────────────────────────────────────────────────────────────────────────
const BRAND_COLOR: [number, number, number] = [224, 123, 57];   // var(--brand-accent, #e07b39)
const DARK_COLOR:  [number, number, number] = [30,  60,  90];   // header bg
const GREEN_COLOR: [number, number, number] = [15,  110, 86];   // #0F6E56

function buildPdf(type: ReportType, row: SampleRegRow): string {
  const doc   = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W     = doc.internal.pageSize.getWidth();
  const now   = new Date();
  const today = fmtDate(now.toISOString());

  // ── header banner ──────────────────────────────────────────────────────────
  doc.setFillColor(...DARK_COLOR);
  doc.rect(0, 0, W, 22, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('ALPACA LIMS', 12, 10);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Laboratory Information Management System', 12, 16);

  // report title tag (orange pill on right)
  const TAG_W = 38;
  doc.setFillColor(...BRAND_COLOR);
  doc.roundedRect(W - TAG_W - 10, 5, TAG_W, 12, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(type, W - TAG_W - 10 + TAG_W / 2, 12.5, { align: 'center' });

  // ── sub-header line ─────────────────────────────────────────────────────────
  doc.setFillColor(245, 245, 245);
  doc.rect(0, 22, W, 10, 'F');
  doc.setTextColor(80, 80, 80);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${today}  ·  Job ID: ${row.jobId}  ·  Status: ${row.statusName} (${row.statusId})`, 12, 28.5);
  doc.setDrawColor(220, 220, 220);
  doc.line(0, 32, W, 32);

  let y = 38;

  // ── section: Job Information ────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK_COLOR);
  doc.text('JOB INFORMATION', 12, y);
  doc.setDrawColor(...BRAND_COLOR);
  doc.setLineWidth(0.6);
  doc.line(12, y + 1.5, 70, y + 1.5);
  y += 6;

  autoTable(doc, {
    startY: y,
    margin: { left: 12, right: 12 },
    theme: 'plain',
    styles: { fontSize: 8.5, cellPadding: { top: 2, bottom: 2, left: 3, right: 3 } },
    columnStyles: {
      0: { fontStyle: 'bold', textColor: [80, 80, 80], cellWidth: 40 },
      1: { textColor: [30, 30, 30], cellWidth: 85 },
      2: { fontStyle: 'bold', textColor: [80, 80, 80], cellWidth: 30 },
      3: { textColor: [30, 30, 30] },
    },
    body: [
      ['Job ID',         row.jobId,                    'Reg Type',     row.registrationType],
      ['Project Name',   row.projectName || '—',        'Status',       row.statusName],
      ['Client',         row.client || '—',             'Status ID',    row.statusId],
      ['Received Date',  fmtDateTime(row.receivedDate), 'Due Date',     fmtDate(row.dueDate)],
    ],
    didParseCell(data) {
      if (data.row.index % 2 === 0) data.cell.styles.fillColor = [250, 250, 250];
    },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

  // ── report-specific section ─────────────────────────────────────────────────
  if (type === 'COC Report') {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...DARK_COLOR);
    doc.text('CHAIN OF CUSTODY', 12, y);
    doc.setDrawColor(...BRAND_COLOR);
    doc.setLineWidth(0.6);
    doc.line(12, y + 1.5, 78, y + 1.5);
    y += 6;

    autoTable(doc, {
      startY: y,
      margin: { left: 12, right: 12 },
      theme: 'striped',
      headStyles: { fillColor: DARK_COLOR, textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 244, 240] },
      head: [['#', 'Sample ID', 'Description', 'Container', 'Preservative', 'Collected', 'Condition']],
      body: [
        ['1', `${row.jobId}-001`, row.projectName || 'Routine Sample', 'Amber 1L', 'H2SO4', fmtDate(row.receivedDate), 'Acceptable'],
        ['2', `${row.jobId}-002`, row.projectName || 'Routine Sample', 'Plastic 500mL', 'None', fmtDate(row.receivedDate), 'Acceptable'],
      ],
    });

    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

    // COC signature block
    doc.setFillColor(248, 248, 248);
    doc.rect(12, y, W - 24, 28, 'F');
    doc.setDrawColor(220, 220, 220);
    doc.rect(12, y, W - 24, 28, 'S');
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('SIGNATURES', 18, y + 6);
    doc.setFont('helvetica', 'normal');
    doc.text('Relinquished by:', 18, y + 13);
    doc.text('Received by:', 18, y + 20);
    doc.setDrawColor(160, 160, 160);
    doc.line(60, y + 14, 120, y + 14);
    doc.line(60, y + 21, 120, y + 21);
    doc.text('Date/Time:', 125, y + 13);
    doc.text('Date/Time:', 125, y + 20);
    doc.line(147, y + 14, W - 14, y + 14);
    doc.line(147, y + 21, W - 14, y + 21);
  }

  if (type === 'Pre Invoice') {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...DARK_COLOR);
    doc.text('PRE-INVOICE SUMMARY', 12, y);
    doc.setDrawColor(...BRAND_COLOR);
    doc.line(12, y + 1.5, 84, y + 1.5);
    y += 6;

    autoTable(doc, {
      startY: y,
      margin: { left: 12, right: 12 },
      theme: 'striped',
      headStyles: { fillColor: DARK_COLOR, textColor: [255, 255, 255], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 244, 240] },
      head: [['#', 'Test Code', 'Description', 'Method', 'Qty', 'Unit Price', 'Amount']],
      body: [
        ['1', 'BA-001', 'Bacteriological Analysis', 'SM 9221E', '1', '$45.00', '$45.00'],
        ['2', 'CH-101', 'Chemical Panel (Basic)', 'EPA 200.7', '1', '$120.00', '$120.00'],
        ['3', 'TC-001', 'Total Coliform', 'SM 9223B', '2', '$35.00', '$70.00'],
      ],
      foot: [['', '', '', '', '', 'SUBTOTAL', '$235.00']],
      footStyles: { fontStyle: 'bold', fillColor: [240, 240, 240], textColor: [30, 30, 30] },
    });

    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(120, 120, 120);
    doc.text('* This is a pre-invoice estimate. Final invoice will be issued upon completion of analysis.', 12, y);
  }

  if (type === 'RTCR') {
    // RTCR — Revised Total Coliform Rule
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...DARK_COLOR);
    doc.text('REVISED TOTAL COLIFORM RULE (RTCR) REPORT', 12, y);
    doc.setDrawColor(...BRAND_COLOR);
    doc.line(12, y + 1.5, 130, y + 1.5);
    y += 6;

    autoTable(doc, {
      startY: y,
      margin: { left: 12, right: 12 },
      theme: 'plain',
      styles: { fontSize: 8.5, cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 } },
      columnStyles: {
        0: { fontStyle: 'bold', textColor: [60, 60, 60], cellWidth: 55 },
        1: { textColor: [20, 20, 20] },
      },
      body: [
        ['PWS Name',             row.client || '—'],
        ['PWS ID',               row.jobId],
        ['Sample Site',          row.projectName || '—'],
        ['Collection Date',      fmtDate(row.receivedDate)],
        ['Report Period',        today],
        ['Coliform Result',      'Total Coliform Absent'],
        ['E. coli Result',       'E. coli Absent'],
        ['Compliance Status',    'IN COMPLIANCE'],
      ],
      didParseCell(data) {
        if (data.column.index === 1 && data.row.index === 7) {
          data.cell.styles.textColor = [...GREEN_COLOR] as [number, number, number];
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.row.index % 2 === 0) data.cell.styles.fillColor = [250, 250, 250];
      },
    });

    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    const rtcrNote = [
      'This report is generated pursuant to 40 CFR Part 141, Subpart Y — Revised Total Coliform Rule.',
      'Results are for informational purposes. Official reporting must be submitted via EPA-approved forms.',
    ];
    rtcrNote.forEach((line, i) => doc.text(line, 12, y + i * 5));
  }

  // ── footer on every page ───────────────────────────────────────────────────
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    const PH = doc.internal.pageSize.getHeight();
    doc.setDrawColor(220, 220, 220);
    doc.line(12, PH - 12, W - 12, PH - 12);
    doc.setTextColor(160, 160, 160);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('ALPACA LIMS · Confidential · For laboratory use only', 12, PH - 7);
    doc.text(`Page ${i} of ${pages}`, W - 12, PH - 7, { align: 'right' });
  }

  return doc.output('bloburl') as unknown as string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
// Render via the shared §14b.6 helpers (local tz, 24-hour, no AM/PM); keep the
// '—' placeholder for empty/unparseable values used in grid cells, CSV + PDFs.
function fmtDateTime(v?: string | null): string {
  return v ? (fmtLocalDateTime(v) || '—') : '—';
}

function fmtDate(v?: string | null): string {
  return v ? (fmtLocalDate(v) || '—') : '—';
}

function cutoffDate(key: DateRangeKey): Date | null {
  if (key === 'All') return null;
  const now = new Date();
  const map: Record<Exclude<DateRangeKey, 'All'>, () => Date> = {
    '1M': () => new Date(now.getFullYear(), now.getMonth() - 1,  now.getDate()),
    '3M': () => new Date(now.getFullYear(), now.getMonth() - 3,  now.getDate()),
    '6M': () => new Date(now.getFullYear(), now.getMonth() - 6,  now.getDate()),
    '1Y': () => new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()),
    '2Y': () => new Date(now.getFullYear() - 2, now.getMonth(), now.getDate()),
    '5Y': () => new Date(now.getFullYear() - 5, now.getMonth(), now.getDate()),
  };
  return map[key as Exclude<DateRangeKey, 'All'>]();
}

/** Submitted = status NAME starts with "Submitted".
 *  Covers both Regular (SR-2-3/4/5) and Prescheduled (SR-1-5) record families. */
function isSubmitted(_statusId: string, statusName: string): boolean {
  return statusName.trim().toLowerCase().startsWith('submitted');
}

/** Pending = status NAME starts with "Pending".
 *  Covers Pending Saving / Submission (SR-2-1/2) AND
 *  Pending Sampling / Transfer (SR-1-1/2) for prescheduled records. */
function isPending(_statusId: string, statusName: string): boolean {
  return statusName.trim().toLowerCase().startsWith('pending');
}

function isCancelled(statusName: string): boolean {
  return statusName.trim().toLowerCase().startsWith('cancel');
}

// Status → shared pill variant (backlog.css .bl-pill ok/warn/alert/info) —
// the page's original severity semantics, rendered through the shared pill
// classes so the Status column is the ONLY coloured cell (BO UI Standard).
function statusPillVariant(statusId: string, statusName: string): string {
  if (isSubmitted(statusId, statusName)) return 'ok';     // green
  if (isPending(statusId, statusName))   return 'warn';   // amber
  if (isCancelled(statusName))           return 'alert';  // red
  return 'info';                                          // hold / default
}


// ─────────────────────────────────────────────────────────────────────────────
// Component — the LIST view (standing rule 10: navigation lands here first).
// New / Edit / row double-click REPLACE it with the full-page detail view.
// ─────────────────────────────────────────────────────────────────────────────
export default function SampleRegistrationPage({ onHome }: { onHome?: () => void } = {}) {
  // ── List ↔ detail routing (the list is replaced, never a popup — rule 12) ──
  const [detail, setDetail] = useState<
    { jobId: string | null; registrationType: string; seed?: DetailSeed; step?: StepIndex } | null
  >(
    // A cross-page link ("edit this job's tests" from Sample Transfer) asked for
    // ONE job — open it instead of the list. Rule 10 governs clicking a NAV
    // entry, where no record was chosen; this is the "select a row" step done
    // from another form, and consumePendingRecord clears so a later sidebar
    // click still lands on the list.
    //
    // Opened WITHOUT waiting for the list: the list is date-range filtered, so
    // the job may not be in `rows` at all. registrationType is a placeholder —
    // the detail GET returns the real one and it wins (mapDetailToHeader).
    () => {
      const jobId = consumePendingRecord('sampreg');
      return jobId ? { jobId, registrationType: 'Regular', step: 1 as StepIndex } : null;
    },
  );
  // Role-Based MAC rights on this form (admins / no-MAC labs fall open).
  const perm = useNavActions('sampreg');
  const [regTypeOpen, setRegTypeOpen] = useState(false);
  const [newRegType, setNewRegType] = useState('Regular');
  // Legacy parity: the New popup's default Registration Type comes from the lab
  // config dbo.SampleSourceSetup (SampleSource: Sampling → "Pre-scheduled",
  // DropOff → "Regular"), NOT a hardcoded 'Regular'. Fetched once on mount.
  const [defaultRegType, setDefaultRegType] = useState('Regular');
  useEffect(() => {
    const ac = new AbortController();
    void api.get('/api/v1/sample-registration/registration-defaults', { signal: ac.signal })
      .then(res => {
        const t = (res.data as { data?: { defaultRegistrationType?: string } })?.data?.defaultRegistrationType;
        if (t === 'Regular' || t === 'Pre-scheduled') setDefaultRegType(t);
      })
      .catch(() => { /* non-fatal: fall back to 'Regular' */ });
    return () => ac.abort();
  }, []);

  const [rows,       setRows]       = useState<SampleRegRow[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [loadMs,     setLoadMs]     = useState<number | null>(null);
  const [error,      setError]      = useState<string | null>(null);
  const [dateRange,  setDateRange]  = useState<DateRangeKey>(DEFAULT_DATE_RANGE.SampleTracking as DateRangeKey);
  const [statusFilt, setStatusFilt] = useState<StatusFilter>('All');
  const [search,     setSearch]     = useState('');
  // AppDataGrid owns sort / per-column filter / pagination; this page keeps only
  // the selection set (driven by AppDataGrid's checkbox column) for the Reports action.
  const [selected,     setSelected]     = useState<Set<string | number>>(new Set());
  const [reportsOpen,  setReportsOpen]  = useState(false);
  const [pdfUrl,       setPdfUrl]       = useState<string | null>(null);
  const [pdfTitle,     setPdfTitle]     = useState('');
  const [generating,   setGenerating]   = useState(false);
  const searchRef  = useRef<HTMLInputElement>(null);
  const reportsRef = useRef<HTMLDivElement>(null);
  const notify     = useNotify();
  const runDelete  = useDeleteFlow();
  const confirm    = useConfirm().confirm;
  const currentUser = useAuthStore(s => s.user?.name ?? 'Service');

  // Close reports dropdown on outside click
  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (reportsRef.current && !reportsRef.current.contains(e.target as Node)) {
        setReportsOpen(false);
      }
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  // Open a report for the single selected row
  const openReport = useCallback((type: ReportType) => {
    setReportsOpen(false);

    const selectedIds = Array.from(selected);
    if (selectedIds.length === 0) {
      notify.warning('Please select a row first.');
      return;
    }
    if (selectedIds.length > 1) {
      notify.warning('Please select only one row to generate a report.');
      return;
    }

    const row = rows.find(r => r.jobId === selectedIds[0]);
    if (!row) return;

    setGenerating(true);
    // Use requestAnimationFrame so the "generating" state renders before the
    // synchronous PDF build blocks the main thread (keeps UI responsive)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          const url = buildPdf(type, row);
          setPdfUrl(url);
          setPdfTitle(`${type} — Job ${row.jobId}`);
          // Post-operation acknowledgement (§18.6 — every operation confirms success).
          notify.success(`${type} generated successfully.`);
        } catch (err) {
          console.error('PDF generation error', err);
          notify.error('Failed to generate report. Please try again.');
        } finally {
          setGenerating(false);
        }
      });
    });
  }, [selected, rows, notify]);

  // Revoke blob URL on close to free memory
  const closePdf = useCallback(() => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfTitle('');
  }, [pdfUrl]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      const co = cutoffDate(dateRange);
      if (co) {
        const pad = (n: number) => String(n).padStart(2, '0');
        params.startDate = `${co.getFullYear()}-${pad(co.getMonth() + 1)}-${pad(co.getDate())}`;
        params.endDate   = `${new Date().getFullYear()}-${pad(new Date().getMonth() + 1)}-${pad(new Date().getDate())}`;
      }
      const res = await api.get('/api/v1/sample-registration', { params });
      setRows((res.data as { data: SampleRegRow[] }).data ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { void load(); }, [load]);

  // ── Derived data ───────────────────────────────────────────────────────────
  // Status chips + global search filter here; AppDataGrid does per-column
  // filter, sort and pagination.
  const filtered = useMemo(() => {
    let r = rows;
    // Status filter — exact StatusDefinition.Status match (G5 parity), not a
    // coarse startsWith bucket, so e.g. "Submitted" excludes "Submitted,
    // Subcontracted" exactly like G5's per-status list filter.
    if (statusFilt !== 'All') r = r.filter(x => (x.statusName || '').trim() === statusFilt);
    // Global search
    if (search.trim()) {
      const t = search.toLowerCase();
      r = r.filter(x =>
        x.jobId.toLowerCase().includes(t) ||
        x.projectName.toLowerCase().includes(t) ||
        x.client.toLowerCase().includes(t) ||
        x.statusName.toLowerCase().includes(t) ||
        x.statusId.toLowerCase().includes(t)
      );
    }
    return r;
  }, [rows, statusFilt, search]);

  // Rows actually visible in the grid — the status/search `filtered` set AFTER
  // the grid's own per-column (Excel-style) filters + sort. The grid reports
  // these via onVisibleRowsChange so "Export to" exports exactly what the user
  // sees, not the full pre-column-filter set (user request 2026-09-26).
  const [visibleRows, setVisibleRows] = useState<SampleRegRow[]>([]);

  // Data-driven status chips (G5 parity): ONE chip per exact StatusDefinition.Status
  // present in the data, each with its EXACT count — so counts match G5's per-status
  // filter (e.g. Submitted 484 · Pending Sampling 99 · Submitted, Subcontracted 6 ·
  // Submitted, Partially Subcontracted 6 · Submitted, Pending Field Data Entry 1 ·
  // On Hold, COD 1) instead of the old coarse startsWith buckets (which merged all
  // "Submitted, …" into one 497 chip and labelled Pending Sampling as just "Pending").
  // Ordered by count desc.
  const statusTabs = useMemo(() => {
    const m = new Map<string, number>();
    for (const x of rows) { const s = (x.statusName || '').trim(); if (s) m.set(s, (m.get(s) ?? 0) + 1); }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [rows]);

  // ── List → detail (prototype: New opens Registration Options first) ─────────
  const seedOf = (r: SampleRegRow): DetailSeed => ({
    client: r.client, projectName: r.projectName, status: r.statusName,
    receivedDate: r.receivedDate, dueDate: r.dueDate,
  });

  const openEdit = useCallback((r: SampleRegRow) => {
    setDetail({ jobId: r.jobId, registrationType: r.registrationType, seed: seedOf(r) });
    window.scrollTo(0, 0);
  }, []);

  const editSelected = useCallback(() => {
    const id = Array.from(selected)[0];
    const row = rows.find(r => r.jobId === id);
    if (row) openEdit(row);
  }, [selected, rows, openEdit]);

  /** Save As from the list (legacy SaveAs_Execute): clone the selected job under a
   *  new Job ID via the same endpoint the detail view uses, then open the clone. */
  const saveAsSelected = useCallback(async () => {
    const jobId = Array.from(selected)[0];
    if (selected.size !== 1 || typeof jobId !== 'string') return;
    const ok = await confirm({ title: 'Save As', message: 'Do you want to save as a new Job ID?' });
    if (!ok) return;
    try {
      const det = await api.get(`/api/v1/sample-registration/${encodeURIComponent(jobId)}`);
      const srcOid: string | undefined = det.data?.data?.oid;
      if (!srcOid) { notify.warning('Save As failed. Please try again.'); return; }
      const res = await api.post(`/api/v1/sample-registration/${srcOid}/save-as`);
      const d = res.data?.data ?? {};
      const newJobId: string = d.JobId ?? d.jobId ?? '';
      notify.success(`${newJobId} JobID created successfully.`);
      setSelected(new Set());
      void load();
    } catch (err: any) {
      notify.warning(err?.response?.data?.error?.message ?? 'Save As failed. Please try again.');
    }
  }, [selected, confirm, notify, load]);

  /** Delete through the shared flow (danger confirm → reason gate → "Deleted.").
   *  Reuses the Sampling Allocation soft-delete endpoint — the SAME one the
   *  detail view's Delete uses (doDeleteJob) — POST /api/v1/sampling/allocation/delete
   *  { jobIds }. `selected` holds human JobIDs (getRowId = r.jobId), which is
   *  exactly what the endpoint expects. The server soft-deletes Samplecheckin.
   *  GCRecord and audits each as "Sampling Allocation". ALPACA parity: a job
   *  whose samples are still logged in is refused 409 "The jobID cannot be
   *  deleted! it is used in sample login." — so a Reset Job ID (which clears the
   *  samples) must come first; a reset job then deletes cleanly, matching ALPACA. */
  const deleteSelected = useCallback(() => {
    const ids = Array.from(selected).map(String);
    if (ids.length === 0) { notify.warning('Please select a row first.'); return; }
    void runDelete({
      formName: SAMPLE_REGISTRATION_FORM,
      message: `Delete ${ids.length} registration${ids.length === 1 ? '' : 's'}?`,
      delete: async () => {
        await api.post('/api/v1/sampling/registration/delete', { jobIds: ids });
      },
      // No custom onError: the shared flow surfaces the API's own message (e.g.
      // the 409 "used in sample login" refusal), matching the detail view.
      onDeleted: () => { setSelected(new Set()); void load(); },
    });
  }, [selected, runDelete, notify, load]);

  // Shared-grid columns — the row edit pencil, JobID, ProjectName, Client,
  // ReceivedDate, RegistrationType, DueDate, Status (the ONLY coloured column),
  // StatusID.
  const cols: AppColumn<SampleRegRow>[] = [
    { field: '__edit', header: '', width: 30, align: 'center',
      sortable: false, filterable: false, noExport: true,
      // Same blue Tabler edit pencil as the shared ModuleListView / Qualifier
      // list row-edit icon (user request 2026-09-23), replacing the faint ✎.
      render: r => (
        // Shared .adg-rowact (NOT the bespoke .sr-edit-cell): the latter keeps
        // 2px vertical padding + vertical-align:middle, which grew the list row
        // to ~30px — taller than every other AppDataGrid list (26px). adg-rowact
        // is the grid's own capped row-action pencil (18px + 4/4 pad = 26px), so
        // this list now matches Analysis Queue et al. (user report 2026-10-01).
        <button className="adg-rowact" title="Edit"
          onClick={e => { e.stopPropagation(); openEdit(r); }}>
          <i className="ti ti-edit" />
        </button>
      ) },
    { field: 'jobId',            header: 'JobID',            width: 100 },
    { field: 'projectName',      header: 'ProjectName',      width: 170 },
    { field: 'client',           header: 'Client',           width: 200 },
    { field: 'receivedDate',     header: 'ReceivedDate',     width: 140,
      render: r => fmtDateTime(r.receivedDate), text: r => fmtDateTime(r.receivedDate) },
    { field: 'registrationType', header: 'RegistrationType', width: 130 },
    { field: 'dueDate',          header: 'DueDate',          width: 100,
      render: r => fmtDate(r.dueDate), text: r => fmtDate(r.dueDate) },
    { field: 'statusName',       header: 'Status',           width: 200,
      text: r => r.statusName,
      render: r => <span className={`bl-pill ${statusPillVariant(r.statusId, r.statusName)}`}>{r.statusName}</span> },
    { field: 'statusId',         header: 'StatusID',         width: 80 },
  ];

  // Phase 2.3 — Sample Registration list key (CLAUDE.md standing rule 14, §18.6,
  // ADR-0013): New/Edit/Delete/Refresh come from the shared <AppToolbar> props,
  // so only the bespoke Reports ▾ button needs registering here — Alt+G mirrors
  // it exactly (same toggle + selected-row gate).  Placed above the `if (detail)`
  // early return so the hook order is unconditional (rules of hooks).
  useHotkeys([
    { combo: 'alt+g', label: 'Reports', group: 'Sample Registration', order: 1,
      run: () => { if (selected.size === 1) setReportsOpen(o => !o); },
      disabled: selected.size !== 1 },
  ]);

  // ── Detail view (New / Edit / row double-click) REPLACES the list ──────────
  // Rendered after every hook above, so hook order stays stable (rules of hooks).
  if (detail) {
    return (
      <SampleRegistrationDetailPage
        key={detail.jobId ?? 'new'}
        jobId={detail.jobId}
        registrationType={detail.registrationType}
        seed={detail.seed}
        initialStep={detail.step}
        siblingIds={filtered.map(r => r.jobId)}
        user={currentUser}
        onHome={() => { setDetail(null); onHome?.(); }}
        onClose={() => setDetail(null)}
        onNavigate={(id) => {
          const row = rows.find(r => r.jobId === id);
          if (row) setDetail({ jobId: id, registrationType: row.registrationType, seed: seedOf(row) });
        }}
        onSaved={() => void load()}
      />
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        /* Reports dropdown (the trigger itself is a shared .ca-btn; only the
           menu is page-specific).  Structural greys via theme tokens. */
        .sr-reports-wrap { position:relative; display:inline-block; }
        .sr-reports-menu {
          position:absolute; top:calc(100% + 4px); left:0;
          min-width:160px; background:#fff; border:1px solid var(--ai-line);
          border-radius:5px; box-shadow:0 4px 14px rgba(0,0,0,.12);
          z-index:200; overflow:hidden;
        }
        .sr-reports-item {
          display:flex; align-items:center; gap:8px;
          padding:8px 14px; font-size:12px; cursor:pointer; color:var(--ai-ink);
          border-bottom:1px solid var(--ai-line-soft); white-space:nowrap;
        }
        .sr-reports-item:last-child { border-bottom:none; }
        .sr-reports-item:hover { background:color-mix(in srgb, var(--brand-accent, #e8670a) 10%, #fff); color:var(--brand-accent-hover, #c04a0a); }
        .sr-reports-item i { font-size:13px; color:var(--ai-muted); }
        .sr-reports-item:hover i { color:var(--brand-accent, #e07b39); }
        .sr-reports-item-rtcr { background:color-mix(in srgb, var(--brand-accent, #e8670a) 10%, #fff); color:var(--brand-accent-hover, #c04a0a); font-weight:600; }
        .sr-reports-item-rtcr i { color:var(--brand-accent, #e07b39); }
        .sr-reports-item-rtcr:hover { background:color-mix(in srgb, var(--brand-accent, #e8670a) 18%, #fff); }

        /* PDF viewer overlay + modal */
        .sr-pdf-overlay {
          position:fixed; inset:0; background:rgba(0,0,0,.6);
          display:flex; align-items:center; justify-content:center;
          z-index:9000; backdrop-filter:blur(2px);
        }
        .sr-pdf-modal {
          width:min(92vw,1100px); height:90vh;
          background:#fff; border-radius:10px;
          display:flex; flex-direction:column;
          box-shadow:0 20px 60px rgba(0,0,0,.4);
          overflow:hidden;
        }
        .sr-pdf-titlebar {
          display:flex; align-items:center; justify-content:space-between;
          padding:10px 16px; background:#1e3c5a; /* token-exempt: white-on-dark hero titlebar */
          flex-shrink:0; gap:12px;
        }
        .sr-pdf-titleleft { display:flex; align-items:center; gap:8px; overflow:hidden; }
        .sr-pdf-titletext {
          font-size:13px; font-weight:600; color:#fff;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
        }
        .sr-pdf-titleright { display:flex; align-items:center; gap:8px; flex-shrink:0; }
        .sr-pdf-dwnbtn {
          display:inline-flex; align-items:center; gap:5px;
          padding:5px 12px; font-size:12px; font-weight:600;
          background:var(--brand-accent, #e07b39); color:#fff; border-radius:5px;
          text-decoration:none; cursor:pointer;
          transition:background .15s;
        }
        .sr-pdf-dwnbtn:hover { background:var(--brand-accent-hover, #c96a2a); }
        .sr-pdf-closebtn {
          width:30px; height:30px; border:none; border-radius:5px;
          background:rgba(255,255,255,.15); color:#fff; cursor:pointer;
          display:flex; align-items:center; justify-content:center; font-size:16px;
        }
        .sr-pdf-closebtn:hover { background:rgba(255,255,255,.3); }
        .sr-pdf-frame { flex:1; border:none; width:100%; }
      `}</style>

      <div className="bl-page">

        {/* ── Fixed top: breadcrumb + title + sub-caption + stage chips ────
            (Prototype list-view header — the KPI tiles were dropped: the stage
            chips below carry the same All / Submitted / Pending counts and the
            prototype trades the tiles for grid rows, "free more space".) ── */}
        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span>
              <i className="ti ti-chevron-right" />
              <span>Sample Tracking</span>
              <i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Sample Registration</span>
            </div>
            {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour
                via navMeta + the TOP load-time chip + the view-only audit chip. */}
            <div className="dp-title-row">
              <FormTitle pageId="sampreg" auditFormName={SAMPLE_REGISTRATION_FORM} loadMs={loadMs} loading={loading} />
            </div>
            <div className="dp-subtitle">
              Register incoming sample jobs, review submissions, and print COC / Pre-Invoice / RTCR
              reports. · {loading ? '…' : rows.length} items · {dateRange}
            </div>
          </div>

          {/* Stage chips — the prototype `.stagechips` row: label + the count in a
              bold span ("All 3"). No "Status:" prefix, no parentheses. */}
          <div className="sa-filter-row" style={{ marginTop: 4 }}>
            {([['All', rows.length] as [string, number], ...statusTabs]).map(([label, count]) => (
              <button key={label} className={`sa-filter-btn${statusFilt === label ? ' active' : ''}`}
                onClick={() => setStatusFilt(label)}>
                {label} <b style={{ marginLeft: 3, opacity: 0.85 }}>{count}</b>
              </button>
            ))}
          </div>
        </div>

        {/* ── Grid area: toolbar + search sit ON the grey page (BO UI Standard
            v2); only the grid card below is white (table scrolls internally). ── */}
        <div className="bl-grid-area">
          {/* Toolbar on its OWN full-width row — canonical order (§18.6):
              [Reports · Date Filter] · New · Edit · Delete · Refresh · Export to. */}
          <div style={{ margin: '0 0 8px' }}>
              <AppToolbar
                left={
                  <>
                    {/* Reports dropdown — trigger is a shared .ca-btn glyph button */}
                    <div className="sr-reports-wrap" ref={reportsRef}>
                      <button
                        type="button"
                        className="ca-btn"
                        disabled={selected.size !== 1}
                        onClick={() => selected.size === 1 && setReportsOpen(o => !o)}
                        title={(selected.size === 1 ? 'Generate report for selected row' : 'Select exactly one row to generate a report') + ` (${formatCombo('alt+g')})`}
                      >
                        <BusyIcon busy={generating} icon="ti-report" />
                        Reports
                        <i className="ti ti-chevron-down" style={{ fontSize: 10 }} />
                      </button>
                      {reportsOpen && (
                        <div className="sr-reports-menu">
                          <div className="sr-reports-item" onClick={() => openReport('COC Report')}>
                            <i className="ti ti-file-text" /> COC Report
                          </div>
                          <div className="sr-reports-item" onClick={() => openReport('Pre Invoice')}>
                            <i className="ti ti-receipt" /> Pre Invoice
                          </div>
                          <div className="sr-reports-item sr-reports-item-rtcr"
                               onClick={() => openReport('RTCR')}>
                            <i className="ti ti-file-certificate" /> RTCR
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                }
                beforeRefresh={
                  <>
                    <ToolbarDivider />
                    {/* Date Filter — legacy parity (prototype places this between
                        Delete and Refresh): the list shows the past three months
                        by default; widen the range to reach older jobs. */}
                    <DateRangeSelect variant="dropdown" label="Date Filter" ranges={TRACKING_RANGES}
                      value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                      onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
                  </>
                }
                // Module Access Control (ALPACA SampleRegIsCreate / IsWrite / IsDelete):
                // New needs Create, Edit needs Write, Delete needs Delete. A row can
                // still be OPENED (pencil / double-click) — read-only without Write.
                onNew={() => { setNewRegType(defaultRegType); setRegTypeOpen(true); }}
                newDisabled={!perm.create}
                onEdit={editSelected}
                editDisabled={selected.size !== 1 || !perm.write}
                onDelete={deleteSelected}
                deleteDisabled={selected.size === 0 || !perm.delete}
                onRefresh={load}
                refreshing={loading}
                // Legacy parity (SaveAsSampleRegistration, list view): Save As sits
                // between Refresh and Export to; enabled for exactly one selected job.
                beforeExport={
                  <button type="button" className="ca-btn" onClick={() => void saveAsSelected()}
                    disabled={selected.size !== 1 || !perm.create}
                    title={!perm.create ? 'Your access level does not allow this action.'
                      : selected.size === 1 ? 'Save the selected job as a new Job ID' : 'Select exactly one job to Save As'}>
                    <span className="tb-i tb-i-blue">📋</span> Save As
                  </button>
                }
                exportData={{ filename: 'sample-registration', ...gridExportMatrix(visibleRows, cols) }}
              />
          </div>

          {/* Global search row — shared .list-search-row on the grey page,
              never inside the card. */}
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input
                ref={searchRef}
                type="text"
                placeholder="Search job, project, client…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <span className="list-search-clear" onClick={() => setSearch('')}>×</span>
              )}
            </div>
            {(filtered.length !== rows.length) && (
              <span className="search-match-count">
                {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
              </span>
            )}
          </div>

          {/* ONE white grid card — the grid + its single shared footer. */}
          <div className="bl-card bl-card-flex">
            {/* Table — load failure stays inline; AppDataGrid handles the rest */}
            {error ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {error}
                <button onClick={load} style={{ marginLeft: 10, color: 'var(--brand-accent, #e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
              </div>
            ) : (
              /* Shared data grid — styling, per-column filters, sort, footer /
                 pagination, load-time and the selection checkboxes are all
                 standardised in <AppDataGrid>.  Double-click a row to open the
                 registration (prototype parity). */
              <AppDataGrid<SampleRegRow>
                rows={filtered}
                columns={cols}
                getRowId={r => r.jobId}
                loading={loading}
                loadMs={loadMs}
                selectable
                selectedIds={selected}
                onSelectionChange={setSelected}
                onRowDoubleClick={openEdit}
                onVisibleRowsChange={setVisibleRows}
                // Legacy parity: the list opens sorted by JobID, newest first.
                initialSort={{ key: 'jobId', dir: 'desc' }}
                defaultPageSize={50}
                emptyText="No matching jobs."
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Registration Options — New asks for the type FIRST.  Prototype-exact:
             title "Registration Options" with NO icon, the "Registration Type:"
             label + Regular / Pre-scheduled radios (Regular default), Cancel · OK. ── */}
      {regTypeOpen && (
        /* `.sr-dlg` gives this popup the prototype's solid accent header + white ✕.
           It is passed as AppDialog's `className` (landing on the portaled
           overlay) — NOT as a wrapper div, which would sit in the page's flow and
           halve the list's height under `.content > * { flex: 1 }`. */
          <AppDialog open title="Registration Options" width={440}
            className="sr-dlg"
            onClose={() => setRegTypeOpen(false)}
            footer={<>
              <button className="ca-btn" onClick={() => setRegTypeOpen(false)}>Cancel</button>
              <button className="ca-btn ca-btn-solid" onClick={() => {
                setRegTypeOpen(false);
                setDetail({ jobId: null, registrationType: newRegType });
                window.scrollTo(0, 0);
              }}>OK</button>
            </>}>
            {/* A picker choice is not unsaved data — Cancel just closes (ALPACA parity). */}
            <div className="sr-regopt" data-unsaved-ignore>
              <span className="sr-regopt-lbl">Registration Type:</span>
              <div className="sr-regopt-row">
                {['Regular', 'Pre-scheduled'].map(t => (
                  <label key={t}>
                    <input type="radio" name="sr-regtype" checked={newRegType === t}
                      onChange={() => setNewRegType(t)} />
                    {t}
                  </label>
                ))}
              </div>
            </div>
          </AppDialog>
      )}

      {/* ── PDF Viewer Modal ─────────────────────────────────────────────────
             Portaled for the same reason as AppDialog: rendered inline it is a
             sibling of `.bl-page` inside the shell's `.content`, and
             `.content > * { flex: 1 }` would halve the list's height behind it. */}
      {pdfUrl && createPortal(
        <div className="sr-pdf-overlay" onClick={e => { if (e.target === e.currentTarget) closePdf(); }}>
          <div className="sr-pdf-modal">
            <div className="sr-pdf-titlebar">
              <div className="sr-pdf-titleleft">
                <i className="ti ti-file-type-pdf" style={{ color: 'var(--brand-accent, #e07b39)', fontSize: 16 }} />
                <span className="sr-pdf-titletext">{pdfTitle}</span>
              </div>
              <div className="sr-pdf-titleright">
                <a
                  href={pdfUrl}
                  download={`${pdfTitle.replace(/[^a-z0-9]/gi, '_')}.pdf`}
                  className="sr-pdf-dwnbtn"
                  title="Download PDF"
                >
                  <i className="ti ti-download" /> Download
                </a>
                <button className="sr-pdf-closebtn" onClick={closePdf} title="Close">
                  <i className="ti ti-x" />
                </button>
              </div>
            </div>
            <iframe
              className="sr-pdf-frame"
              src={pdfUrl}
              title={pdfTitle}
            />
          </div>
        </div>,
        document.body,
      )}

      {/* Generating overlay — portaled, same reason as the PDF modal above. */}
      {generating && createPortal(
        <div className="sr-pdf-overlay" style={{ background: 'rgba(0,0,0,.35)' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '28px 40px', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,.2)' }}>
            <LoaderSymbol size={32} />
            <div style={{ marginTop: 12, fontSize: 14, color: 'var(--ai-ink)', fontWeight: 600 }}>Building PDF…</div>
            <div style={{ marginTop: 4, fontSize: 12, color: 'var(--ai-muted)' }}>This will open instantly</div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
